import { useTranslation } from 'react-i18next';
import { IconButton, IconTile, Button, ProgressBar } from '@teo/components';
import {
  Training,
  Theme,
  Evaluation,
  Flag,
  DuoToneCourseFilled,
  DuoToneAssessmentFilled,
  DuoToneVacancyFilled,
  TaskCheckmarkFilled,
  ChevronDown,
  ChevronRight,
  CloseCircleFilled,
} from '@teo/components/icons';
import { Blocked } from '@/components/Images/Blocked';
import { categoriesIcon, sortDataLP } from '@/components/LearningPath/util';
import { toPercent } from '@/util';
import { useGet } from '@/query/fetchHooks';
import { useState, useEffect, useRef } from 'react';
import { axios } from '@/axios';
import { config } from '@/config';
import { useNavigate } from 'react-router-dom';
import i18n from 'i18next';

const CorrectionItem = ({
  nds,
  Icon,
  studentId,
  organisationId,
  subContent = false,
  lpgroupId,
  startEvaluation,
  setEvaluationContent,
  student,
  setParentOpenTheme,
  setNotPassedList,
  setListNotPassedText,
}) => {
  const { t } = useTranslation();
  const [themeItems, setThemeItems] = useState(null);
  const [height, setHeight] = useState(0);
  const [themeProgress, setThemeProgress] = useState(0);
  const [isOpenTheme, setIsOpenTheme] = useState(false);
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const correctionBasePath = `/${i18n.language}/follow-up/${organisationId}/correction`;

  useEffect(() => {
    if (isOpenTheme && contentRef.current) {
      setHeight(contentRef.current.scrollHeight + 12);
    } else {
      setHeight(0);
    }
  }, [isOpenTheme, startEvaluation]);

  useGet(`/lpnodestatuses`, {
    enabled: nds?.type === 'learningpath',
    lpgroupId,
    learningpathId: nds?.objectId,
    userId: studentId,
    onSuccess: async (res) => {
      const nodedetails = res?.data?.filter((el) => el?.type !== 'root');

      const nodedetailsStatus = nodedetails?.map((el) => {
        if (el?.progress === 1) {
          if (el?.isCorrected === true) {
            if (
              el?.subType === 'milestone' ||
              el?.minScore === 0 ||
              el.score / el.maxScore >= el.minScore ||
              (el?.score === 0 && el.maxScore === 0)
            ) {
              return { ...el, done: true, pass: true, needCheck: false };
            } else {
              return { ...el, done: true, pass: false, needCheck: false };
            }
          } else {
            return { ...el, done: true, pass: false, needCheck: true };
          }
        } else {
          return { ...el, done: false, pass: false, needCheck: false };
        }
      });
      if (nodedetails?.length > 0) {
        const sortData = sortDataLP(nodedetailsStatus);
        setThemeItems(sortData);
      }
    },
  });

  useEffect(() => {
    if (themeItems?.length > 0 && nds?.type === 'learningpath') {
      const nodesDone = themeItems?.filter((el) => el.progress == 1);

      const progress = nodesDone?.length / themeItems?.length;
      setThemeProgress(progress);

      const list = themeItems?.filter(
        (item) =>
          item?.done && !item?.pass && !item?.needCheck && item?.isCorrected
      );

      const listText = list
        .map(
          (item) =>
            `${t('pages.lp_landing.' + item.subType)} <b>${item.name}</b>`
        )
        .join(', ');

      setNotPassedList((prev) => [...prev, ...list]);
      setListNotPassedText((prev) => prev + listText);
    }
  }, [themeItems]);

  useEffect(() => {
    if (
      subContent &&
      ((nds?.done && nds?.needCheck && !student?.isBlocked) ||
        (!nds?.pass && nds?.isCorrected))
    ) {
      setParentOpenTheme(true);
    }
  }, [subContent, nds]);

  return (
    <>
      <div
        className={`flex w-full flex-col items-center rounded-xl border p-3 ${
          !nds?.mandatory && 'border-dashed'
        } ${subContent && ' border border-grey-01 bg-grey-01'} ${
          nds?.done &&
          !nds?.pass &&
          !nds?.needCheck &&
          nds?.isCorrected &&
          '!border-error-02'
        }`}
      >
        <div className="flex w-full items-center">
          {nds?.type === 'learningpath' && (
            <Button
              className="mr-2 !px-1"
              Icon={isOpenTheme ? ChevronDown : ChevronRight}
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsOpenTheme(!isOpenTheme);
              }}
            >
              {isOpenTheme ? (
                <ChevronDown className="w-4 text-grey-05" />
              ) : (
                <ChevronRight className="w-4 text-grey-05" />
              )}
            </Button>
          )}
          <IconTile
            Icon={
              nds?.subType === 'training'
                ? Training
                : nds?.subType === 'learningpath'
                ? Icon
                : nds?.subType === 'milestone'
                ? Flag
                : nds?.subType === 'evaluation'
                ? Evaluation
                : nds?.subType === 'course'
                ? DuoToneCourseFilled
                : nds?.subType === 'assessment'
                ? DuoToneAssessmentFilled
                : nds?.subType === 'leervacature'
                ? DuoToneVacancyFilled
                : DuoToneCourseFilled
            }
            imageUrl={nds?.picture}
            color={
              nds?.subType === 'training'
                ? 'green'
                : nds?.subType === 'learningpath'
                ? 'dark_blue'
                : nds?.subType === 'milestone'
                ? 'blue'
                : nds?.subType === 'evaluation'
                ? 'purple'
                : nds?.subType === 'course'
                ? 'yellow'
                : nds?.subType === 'assessment'
                ? 'red'
                : nds?.subType === 'leervacature'
                ? 'yellow'
                : 'yellow'
            }
            size="small_md"
            className={
              nds?.subType === 'course'
                ? 'fill-primary-06 text-primary-01'
                : nds?.subType === 'assessment'
                ? 'fill-tint-red-dark text-tint-red-light'
                : nds?.subType === 'leervacature' &&
                  'fill-error-04 text-error-04'
            }
          />
          <div className="flex flex-1 flex-col gap-1 px-3">
            <p className="text-xs text-grey-06">
              {nds?.subType === 'training'
                ? t('pages.learning_path.training')
                : nds?.subType === 'learningpath'
                ? t('pages.learning_path.theme')
                : nds?.subType === 'milestone'
                ? t('pages.learning_path.milestone')
                : nds?.subType === 'evaluation'
                ? t('pages.learning_path.evaluation')
                : nds?.subType === 'course'
                ? t('links.course')
                : nds?.subType === 'assessment'
                ? t('links.assessment')
                : nds?.subType === 'leervacature'
                ? t('links.vacancy')
                : t('links.course')}
            </p>
            <p
              className={`max-w-[225px] break-words text-left text-sm font-medium text-black line-clamp-2 `}
            >
              {nds?.name}
            </p>
          </div>
          {nds?.type !== 'learningpath' ? (
            <>
              {!nds?.done && !nds?.pass && !nds?.needCheck ? (
                nds?.progress > 0 ? (
                  <div className="flex w-auto items-center justify-end">
                    <div className="flex w-[160px]">
                      <ProgressBar
                        max={100}
                        value={Math.round(nds?.progress * 100)}
                        className="h-1 overflow-hidden rounded-lg"
                      />
                    </div>
                    <span className="pl-3 text-xs text-grey-06">
                      {Math.round(nds?.progress * 100)}%
                    </span>
                  </div>
                ) : (
                  <div className="flex flex-row items-center gap-2 text-grey-07">
                    <Blocked className="h-4 w-4" />
                    <p className="text-sm">
                      {t('pages.follow_learningpath.locked')}
                    </p>
                  </div>
                )
              ) : (
                <>
                  {nds?.done &&
                    !nds?.needCheck &&
                    (nds?.minScore <= 0 ||
                      nds?.minScore === 1 ||
                      nds?.subType === 'milestone' ||
                      (nds?.pass &&
                        nds?.score === 0 &&
                        nds?.maxScore === 0)) && (
                      <>
                        {nds?.pass ? (
                          <TaskCheckmarkFilled className="h-8 w-8 text-success-05" />
                        ) : (
                          <CloseCircleFilled className="ml-7 h-8 w-8 text-error-04" />
                        )}
                      </>
                    )}

                  {nds?.done && nds?.needCheck && !student?.isBlocked && (
                    <Button
                      onClick={async (ev) => {
                        if (nds?.type === 'course') {
                          ev.stopPropagation();
                          const result = await axios.get(
                            `${config.teodoc}/results/list2?submitted=true&courseId=${nds.objectId}&userId=${studentId}&offset=0&count=1`
                          );
                          if (result?.data.length > 0) {
                            navigate(
                              `${correctionBasePath}/${nds.objectId}/${studentId}/${result?.data[0]?.lessonId}?lpgroupId=${lpgroupId}`
                            );
                          }
                        } else if (nds?.type === 'training') {
                          ev.stopPropagation();
                          navigate(
                            `${correctionBasePath}/training/${nds.objectId}/${studentId}`
                          );
                        }
                      }}
                    >
                      {nds?.type === 'training'
                        ? t('pages.lp_correction.start_evaluation')
                        : t('pages.follow_up_courses.improve_button')}
                    </Button>
                  )}

                  {nds?.done &&
                    !nds?.needCheck &&
                    nds?.minScore > 0 &&
                    nds?.minScore !== 1 &&
                    nds?.subType !== 'milestone' &&
                    !(nds?.pass && nds?.score === 0 && nds?.maxScore === 0) && (
                      <>
                        {nds?.type === 'course' && (
                          <div className="mr-3">
                            <Button
                              size="sm"
                              onClick={(ev) => {
                                ev.stopPropagation();
                                const resultPath = `/${i18n.language}/follow-up/${organisationId}/result/${nds.objectId}/${studentId}/lesson?lpgroupId=${lpgroupId}`;
                                navigate(resultPath);
                              }}
                              variant="outline"
                              className={`${
                                i18n.language === 'fr' &&
                                'w-max min-w-min max-w-[110px]'
                              }`}
                            >
                              {t('pages.follow_up_courses.view_answers_button')}
                            </Button>
                          </div>
                        )}
                        {nds?.pass ? (
                          <div className="ml-auto flex items-center gap-1 text-sm text-grey-07">
                            {t('columns.score')}:{' '}
                            <span className="font-semibold">
                              {toPercent(nds.score, nds.maxScore) && nds.score
                                ? toPercent(nds.score, nds.maxScore)
                                : 0}
                              %
                            </span>
                            <TaskCheckmarkFilled className="ml-7 h-8 w-8 text-success-05" />
                          </div>
                        ) : (
                          <>
                            <div className="ml-auto flex items-center gap-1 text-sm text-error-04">
                              {t('columns.score')}:{' '}
                              <span className="font-semibold">
                                {toPercent(nds.score, nds.maxScore) && nds.score
                                  ? toPercent(nds.score, nds.maxScore)
                                  : 0}
                                %
                              </span>
                              <CloseCircleFilled className="ml-7 h-8 w-8 text-error-04" />
                            </div>
                          </>
                        )}
                      </>
                    )}
                </>
              )}
            </>
          ) : (
            <>
              {themeProgress > 0 ? (
                <>
                  {themeProgress == 1 ? (
                    ''
                  ) : (
                    <div className="flex w-auto items-center justify-end">
                      <div className="flex w-[160px]">
                        <ProgressBar
                          max={100}
                          value={Math.round(themeProgress * 100)}
                          className="h-1 overflow-hidden rounded-lg"
                        />
                      </div>
                      <span className="pl-3 text-xs text-grey-06">
                        {Math.round(themeProgress * 100)}%
                      </span>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-row items-center gap-2 text-grey-07">
                  <Blocked className="h-4 w-4" />
                  <p className="text-sm">
                    {t('pages.follow_learningpath.locked')}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {nds?.type === 'learningpath' && (
          <div
            className="w-full"
            style={{
              overflow: 'hidden',
              maxHeight: height,
              transition: 'max-height 0.4s ease',
            }}
          >
            {themeItems?.length > 0 && (
              <div className={`mt-3 flex flex-col gap-3`} ref={contentRef}>
                {themeItems?.map((item) => {
                  return (
                    <CorrectionItem
                      key={item?.id}
                      nds={item}
                      Icon={Icon}
                      studentId={studentId}
                      organisationId={organisationId}
                      lpgroupId={lpgroupId}
                      subContent={true}
                      startEvaluation={startEvaluation}
                      setEvaluationContent={setEvaluationContent}
                      setParentOpenTheme={setIsOpenTheme}
                      setNotPassedList={setNotPassedList}
                      setListNotPassedText={setListNotPassedText}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      {nds?.done &&
        !nds?.pass &&
        !nds?.needCheck &&
        nds?.isCorrected &&
        startEvaluation &&
        nds?.type !== 'learningpath' && (
          <div className="flex w-full flex-col items-center rounded-xl border border-grey-06 p-3 ">
            <div className="flex w-full items-center">
              <IconTile Icon={Evaluation} color={'purple'} size="small_md" />
              <div className="flex flex-1 flex-col gap-1 px-3">
                <p className="text-xs text-grey-06">
                  {t('pages.lp_correction.lp_evaluation')}
                </p>
                <p
                  className={`max-w-[400px] break-words text-left text-sm font-medium text-black line-clamp-2 `}
                >
                  {t('pages.lp_correction.lp_hold')}
                </p>
              </div>
              <Button
                onClick={() =>
                  setEvaluationContent({
                    open: true,
                    node: nds,
                    studentId: studentId,
                    Icon: Icon,
                  })
                }
              >
                {t('pages.lp_correction.start_evaluation')}
              </Button>
            </div>
          </div>
        )}
    </>
  );
};

const CorrectionContent = ({
  nodeStatuses,
  allCategories,
  studentId,
  organisationId,
  lpgroupId,
  setEvaluationContent,
  student,
}) => {
  const { t } = useTranslation();
  const [notPassedList, setNotPassedList] = useState(null);
  const [startEvaluation, setStartEvaluation] = useState(false);
  const [listNotPassedText, setListNotPassedText] = useState(null);

  useEffect(() => {
    const list = nodeStatuses?.filter(
      (item) =>
        item?.done && !item?.pass && !item?.needCheck && item?.isCorrected
    );
    const listText = list
      .map(
        (item) => `${t('pages.lp_landing.' + item.subType)} <b>${item.name}</b>`
      )
      .join(', ');
    setListNotPassedText(listText);
    setNotPassedList(list);
  }, [nodeStatuses]);

  if (!nodeStatuses) return null;
  return (
    <div className="flex w-full px-4">
      <div className="mx-auto my-6 flex w-full max-w-4xl flex-col gap-3">
        {notPassedList?.length > 0 && !student?.isBlocked && (
          <div className="my-2">
            <div className="flex w-full flex-col gap-1 rounded-lg bg-error-01 p-5">
              <CloseCircleFilled className="h-5 w-5 text-error-04" />
              <p
                className="text-sm text-error-05"
                dangerouslySetInnerHTML={{
                  __html: t('pages.lp_correction.failed_message', {
                    list: listNotPassedText,
                  }),
                }}
              ></p>
              {!startEvaluation && (
                <div className="mt-2">
                  <Button
                    variant="outline"
                    onClick={() => setStartEvaluation(true)}
                  >
                    {t('pages.lp_correction.start_evaluation')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
        {student?.isBlocked && (
          <div className="my-2">
            <div className="flex w-full flex-col gap-1 rounded-lg bg-error-01 p-5">
              <CloseCircleFilled className="h-5 w-5 text-error-04" />
              <p
                className="text-sm text-error-05"
                dangerouslySetInnerHTML={{
                  __html: t(`pages.follow_learningpath.notPassLP`),
                }}
              ></p>
            </div>
          </div>
        )}
        {nodeStatuses?.map((nds) => {
          let Icon;

          if (nds?.tags && nds?.type === 'learningpath') {
            const learningpathTags = allCategories?.find(
              (el) => el?.label === nds?.tags.split(':')[1]
            );
            Icon = categoriesIcon(learningpathTags?.icon);
          } else {
            Icon = Theme;
          }
          return (
            <CorrectionItem
              key={nds?.id}
              nds={nds}
              Icon={Icon}
              studentId={studentId}
              organisationId={organisationId}
              lpgroupId={lpgroupId}
              startEvaluation={startEvaluation}
              setEvaluationContent={setEvaluationContent}
              student={student}
              setNotPassedList={setNotPassedList}
              setListNotPassedText={setListNotPassedText}
            />
          );
        })}
      </div>
    </div>
  );
};
export default CorrectionContent;
